import { h } from "preact";
import { memo } from "preact/compat";
import { useState, useContext, useRef } from "preact/hooks";

import CommentsAction from "./CommentsAction";
import LoadReplies from "./LoadReplies";
import ReplyBox from "./ReplyBox";

import {
  addOverlay,
  reactionsCount,
  removeOverlay,
  timeAgo,
} from "../lib/helper";
import {
  useFirestore,
  useFirestoreDocData,
  useOnClickOutside,
  useUser,
} from "../lib/preactfire";

import { Context, setUserInfoAction } from "../lib/globalState";
import ContextMenu from "./ContextMenu";
import UserReactionMenu from "./UserReactionMenu";

const Reply = memo(
  ({ id, posterName, setLoadingReplies, setRepliesLoaded, toggle }) => {
    const { dispatch } = useContext(Context);

    const user = useUser();

    const reply = useFirestoreDocData(
      useFirestore().collection("comment").doc(id)
    );
    const repliesArray = reply?.replies?.sort((a, b) =>
      a.timestamp < b.timestamp ? -1 : b.timestamp < a.timestamp ? 1 : 0
    );

    const [_showReplies, _loadReplies] = useState(false);
    const [_loadingReplies, _setLoadingReplies] = useState(false);
    const [_repliesLoaded, _setRepliesLoaded] = useState(false);
    const [_toggle, _setToggle] = useState(false);
    const [toggleReaction, setToggleReaction] = useState(false);

    const wrapperRef = useRef();

    const replyPosterName = reply?.user_name;

    useOnClickOutside(wrapperRef, () => setToggleReaction(false));

    if (
      reply?.flags?.filter((flag) => flag?.user_id === user?.uid)?.length > 0
    ) {
      return null;
    }

    if (reply && !toggle) {
      setLoadingReplies(false);
      setRepliesLoaded(true);
      return (
        <div className="reply-item">
          <div className="comment-content-wrapper">
            <div className="comment-user">
              <img src={reply.profile_img} className="comment-user-img" />
              <div className="comment-user-wrapper">
                <span
                  className="comment-user-name"
                  id="comment-user-name"
                  onClick={() => {
                    removeOverlay();
                    dispatch(
                      setUserInfoAction(
                        {
                          name: reply.user_name,
                          profile_img: reply.profile_img,
                          replies: reply.replies,
                          user_id: reply.user_id,
                        },
                        true
                      )
                    );
                  }}
                >
                  {reply.user_name}
                </span>
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"
                    class="user-replier-arrow"
                  >
                    <path d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z" />
                  </svg>
                </span>
                <span className="comment-replier-name">{posterName}</span>
                <span className="comment-ts">
                  {timeAgo(
                    reply.timestamp < 0 ? -reply.timestamp : reply.timestamp
                  )}
                </span>
              </div>

              <div
                className="right-menu"
                onClick={(e) => {
                  if (e.currentTarget.classList.contains("show-right-menu")) {
                    document.querySelectorAll(".right-menu").forEach((el) => {
                      el.classList.remove("show-right-menu");
                    });
                    e.currentTarget.classList.remove("show-right-menu");
                    removeOverlay();
                  } else {
                    document.querySelectorAll(".right-menu").forEach((el) => {
                      el.classList.remove("show-right-menu");
                    });
                    e.currentTarget.classList.add("show-right-menu");
                    addOverlay();
                  }
                }}
              >
                <svg viewBox="0 0 128 512">
                  <path d="M64 208c26.5 0 48 21.5 48 48s-21.5 48-48 48-48-21.5-48-48 21.5-48 48-48zM16 104c0 26.5 21.5 48 48 48s48-21.5 48-48-21.5-48-48-48-48 21.5-48 48zm0 304c0 26.5 21.5 48 48 48s48-21.5 48-48-21.5-48-48-48-48 21.5-48 48z" />
                </svg>

                <ContextMenu obj={reply} />
              </div>
            </div>

            <div
              class="comment-text"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: reply.content }}
            />
            <div className="comment-action sub-action">
              <div
                id="comment_action_cointainer"
                className="comment-share-cointainer"
              >
                <CommentsAction id={reply.id} obj={reply} />
                <div className="d-flex">
                  {reactionsCount(reply) > 0 && (
                    <div
                      ref={wrapperRef}
                      className="d-flex"
                      onClick={() => {
                        addOverlay();
                        setToggleReaction(!toggleReaction);
                      }}
                    >
                      {toggleReaction && (
                        <UserReactionMenu obj={reply} isReply={true} />
                      )}
                      <span className="show-hide-reply">
                        <svg
                          viewBox="0 0 512 512"
                          style="height: 0.75rem; margin-right: 0.25rem"
                        >
                          <path d="M437.02 74.98C388.668 26.63 324.38 0 256 0S123.332 26.63 74.98 74.98 0 187.62 0 256s26.628 132.668 74.98 181.02S187.62 512 256 512s132.668-26.63 181.02-74.98S512 324.38 512 256s-26.628-132.668-74.98-181.02zM256 481.524c-124.354 0-225.524-101.17-225.524-225.524S131.646 30.476 256 30.476 481.524 131.646 481.524 256 380.354 481.524 256 481.524zm-55.378-293.128c-24.953-24.955-65.556-24.953-90.5 0a15.24 15.24 0 0 0 0 21.55c5.952 5.95 15.6 5.95 21.55 0 13.072-13.07 34.34-13.07 47.4 0a15.19 15.19 0 0 0 10.774 4.464c3.9 0 7.8-1.488 10.774-4.464a15.24 15.24 0 0 0 0-21.55zm201.262 0c-24.953-24.953-65.556-24.955-90.5 0a15.24 15.24 0 0 0 0 21.55c5.952 5.95 15.6 5.95 21.55 0 13.07-13.07 34.338-13.072 47.4 0a15.19 15.19 0 0 0 10.774 4.464c3.9 0 7.8-1.488 10.774-4.464a15.24 15.24 0 0 0 0-21.55zm-10.774 78.78H120.9a15.24 15.24 0 0 0-15.238 15.238c0 82.902 67.447 150.35 150.35 150.35s150.35-67.447 150.35-150.35a15.24 15.24 0 0 0-15.238-15.238zM256 402.286c-60.938 0-111.402-45.703-118.9-104.635h237.8c-7.508 58.932-57.972 104.635-118.9 104.635z" />
                        </svg>
                        {reactionsCount(reply)}
                      </span>
                    </div>
                  )}
                  <LoadReplies
                    replies={reply.replies}
                    loadReplies={_loadReplies}
                    loadingReplies={_loadingReplies}
                    setLoadingReplies={_setLoadingReplies}
                    repliesLoaded={_repliesLoaded}
                    toggle={_toggle}
                    setToggle={_setToggle}
                  />
                </div>
              </div>
            </div>
            {/* Reply Box */}
            <ReplyBox id={reply.id} posterName={reply.user_name} />

            {/* Replies Wrapper */}
            {_showReplies && (
              <div
                class="reply-wrapper"
                style={(!_repliesLoaded || _toggle) && "margin-top: 0"}
              >
                {repliesArray?.map((_reply, index) => (
                  <Reply
                    key={index}
                    userId={reply?.user_id}
                    posterName={replyPosterName}
                    setLoadingReplies={_setLoadingReplies}
                    setRepliesLoaded={_setRepliesLoaded}
                    toggle={_toggle}
                    id={_reply?.comment_id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  }
);

export default Reply;
